/**
 * Personal Info Form Component
 *
 * Form for editing user personal information with validation
 */

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useTranslation } from 'react-i18next';
import { accountService } from '../../services/accountService';
import { addressService, type Province, type Commune } from '../../services/addressService';

const personalInfoSchema = z.object({
  firstName: z.string().min(1, 'Họ không được để trống'),
  lastName: z.string().min(1, 'Tên không được để trống'),
  phone: z
    .string()
    .regex(/^[0-9\s-+()]+$/, 'Số điện thoại không hợp lệ')
    .min(10, 'Số điện thoại phải có ít nhất 10 ký tự'),
  dateOfBirth: z.string().optional(),
  gradeLevel: z.string().optional(),
  school: z.string().optional(),
  provinceCode: z.string().optional(),
  communeCode: z.string().optional(),
});

type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;

export function PersonalInfoForm() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Ref to skip the next commune fetch effect (used when loading profile)
  const skipCommuneFetch = useRef(false);

  // Address API states
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: (user as any)?.phone || '',
      dateOfBirth: '',
      gradeLevel: '',
      school: '',
      provinceCode: '',
      communeCode: '',
    },
  });

  // Load provinces on mount
  useEffect(() => {
    const loadProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const data = await addressService.getProvinces();
        setProvinces(data);
      } catch (error) {
        console.error('Failed to load provinces:', error);
      } finally {
        setLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await accountService.getProfile();

        let profileCommunes: Commune[] = [];

        // If user has province, load communes immediately before resetting form
        if (profile.provinceCode) {
          try {
            profileCommunes = await addressService.getCommunes(profile.provinceCode);
            setCommunes(profileCommunes);

            // Set logic to skip the effect-based fetch since we just fetched
            skipCommuneFetch.current = true;
            setSelectedProvinceCode(profile.provinceCode);
          } catch (error) {
            console.error('Failed to load initial communes:', error);
          }
        }

        // Set form values
        reset({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth
            ? new Date(profile.dateOfBirth).toISOString().split('T')[0]
            : '',
          gradeLevel: profile.gradeLevel || '',
          school: profile.school || '',
          provinceCode: profile.provinceCode || '',
          communeCode: profile.communeCode || '',
        });
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    loadProfile();
  }, [reset, setValue]);

  // Load communes when province changes
  useEffect(() => {
    const loadCommunes = async () => {
      // Check if we should skip this fetch (handled by initial load)
      if (skipCommuneFetch.current) {
        skipCommuneFetch.current = false;
        return;
      }

      if (!selectedProvinceCode) {
        setCommunes([]);
        return;
      }

      setLoadingCommunes(true);
      try {
        const data = await addressService.getCommunes(selectedProvinceCode);
        setCommunes(data);
      } catch (error) {
        console.error(`Failed to load communes for province ${selectedProvinceCode}:`, error);
        setCommunes([]);
      } finally {
        setLoadingCommunes(false);
      }
    };
    loadCommunes();
  }, [selectedProvinceCode]);

  // Watch for changes
  watch(() => setIsDirty(true));

  const onSubmit = async (data: PersonalInfoFormData) => {
    try {
      setSaveError(null);

      // Debug: Log form data
      console.log('Form data:', {
        provinceCode: data.provinceCode,
        communeCode: data.communeCode,
        selectedProvinceCode,
        communesLength: communes.length,
      });

      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        gradeLevel: data.gradeLevel,
        school: data.school,
        provinceCode: data.provinceCode,
        provinceName: provinces.find(p => p.code === data.provinceCode)?.name,
        communeCode: data.communeCode,
        communeName: communes.find(c => c.code === data.communeCode)?.name,
      };

      console.log('Sending to API:', updateData);

      await accountService.updateProfile(updateData);
      setSaveSuccess(true);
      setIsDirty(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      setSaveError(error.message || 'Có lỗi xảy ra khi cập nhật thông tin');
    }
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm(t('account.personal.confirmCancel'))) {
      return;
    }
    reset();
    setIsDirty(false);
    setSaveError(null);
    setSaveSuccess(false);
  };

  return (
    <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 md:p-8 shadow-sm'>
      <div className='flex items-center justify-between mb-6'>
        <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2'>
          <span className='material-symbols-outlined text-primary'>badge</span>
          {t('account.personal.title')}
        </h3>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Name */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.firstName')} & {t('account.personal.lastName')}
            </label>
            <div className='grid grid-cols-2 gap-3'>
              <input
                {...register('firstName')}
                className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base'
                type='text'
                placeholder={t('account.personal.firstName')}
              />
              <input
                {...register('lastName')}
                className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base'
                type='text'
                placeholder={t('account.personal.lastName')}
              />
            </div>
            {(errors.firstName || errors.lastName) && (
              <p className='text-xs text-red-600 dark:text-red-400'>
                {errors.firstName?.message || errors.lastName?.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.email')}
            </label>
            <input
              className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light/50 dark:bg-background-dark/50 text-text-secondary dark:text-text-secondary-dark px-4 py-3 text-base cursor-not-allowed'
              disabled
              type='email'
              value={user?.email || ''}
            />
            <p className='text-xs text-text-secondary dark:text-text-secondary-dark mt-1'>
              {t('account.personal.emailNote')}
            </p>
          </div>

          {/* Phone */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.phone')}
            </label>
            <input
              {...register('phone')}
              className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base'
              type='tel'
              placeholder='0987 654 321'
            />
            {errors.phone && (
              <p className='text-xs text-red-600 dark:text-red-400'>{errors.phone.message}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.dob')}
            </label>
            <input
              {...register('dateOfBirth')}
              className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base'
              type='date'
            />
          </div>

          {/* Education Level */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.educationLevel')}
            </label>
            <div className='relative'>
              <select
                {...register('gradeLevel')}
                className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base appearance-none'
              >
                <option value=''>{t('account.personal.selectEducationLevel')}</option>
                <option value='THCS'>{t('account.personal.levels.thcs')}</option>
                <option value='THPT'>{t('account.personal.levels.thpt')}</option>
                <option value='UNIVERSITY'>{t('account.personal.levels.university')}</option>
                <option value='WORKING'>{t('account.personal.levels.working')}</option>
              </select>
              <span className='material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none'>
                expand_more
              </span>
            </div>
          </div>

          {/* School */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.school')}
            </label>
            <input
              {...register('school')}
              className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base'
              type='text'
              placeholder={t('account.personal.school')}
            />
          </div>

          {/* Province */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.province')}
            </label>
            <div className='relative'>
              <select
                {...register('provinceCode', {
                  onChange: e => {
                    const code = e.target.value;
                    setSelectedProvinceCode(code);
                    // Reset commune when province changes
                    setValue('communeCode', '');
                  },
                })}
                disabled={loadingProvinces}
                className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base appearance-none disabled:opacity-50'
              >
                <option value=''>{t('account.personal.selectProvince')}</option>
                {provinces.map(province => (
                  <option key={province.code} value={province.code}>
                    {province.name}
                  </option>
                ))}
              </select>
              <span className='material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none'>
                expand_more
              </span>
            </div>
            {loadingProvinces && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {t('account.personal.loadingProvinces')}
              </p>
            )}
          </div>

          {/* Commune */}
          <div className='space-y-2'>
            <label className='block text-sm font-medium text-text-main dark:text-text-main-dark'>
              {t('account.personal.commune')}
            </label>
            <div className='relative'>
              <select
                {...register('communeCode')}
                disabled={!selectedProvinceCode || loadingCommunes}
                className='w-full rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark focus:border-primary focus:ring-primary px-4 py-3 text-base appearance-none disabled:opacity-50'
              >
                <option value=''>{t('account.personal.selectCommune')}</option>
                {communes.map(commune => (
                  <option key={commune.code} value={commune.code}>
                    {commune.name}
                  </option>
                ))}
              </select>
              <span className='material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none'>
                expand_more
              </span>
            </div>
            {loadingCommunes && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {t('account.personal.loadingCommunes')}
              </p>
            )}
            {!selectedProvinceCode && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {t('account.personal.selectProvinceFirst')}
              </p>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {saveSuccess && (
          <div className='rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-4'>
            <p className='text-sm text-green-700 dark:text-green-400 font-medium'>
              {t('account.personal.success')}
            </p>
          </div>
        )}

        {saveError && (
          <div className='rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4'>
            <p className='text-sm text-red-700 dark:text-red-400 font-medium'>{saveError}</p>
          </div>
        )}

        {/* Page Actions */}
        <div className='flex items-center justify-end gap-4 py-4 pt-6 border-t border-border-light dark:border-border-dark'>
          <button
            type='button'
            onClick={handleCancel}
            disabled={isSubmitting}
            className='px-6 py-3 rounded-lg text-text-main dark:text-text-main-dark font-bold text-sm hover:bg-black/5 dark:hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50'
          >
            {t('account.personal.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting || !isDirty}
            className='px-8 py-3 rounded-lg bg-primary text-text-main font-bold text-sm shadow-sm hover:bg-primary/90 hover:shadow-md transition-all transform hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
          >
            {isSubmitting ? t('account.personal.saving') : t('account.personal.save')}
          </button>
        </div>
      </form>
    </section>
  );
}
